import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { quizQuestionsApi, rulesApi } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Loading from '../components/Loading';
import ConfirmModal from '../components/ConfirmModal';
import './QuizQuestions.css';

function QuizQuestions() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [formData, setFormData] = useState({
    question: '',
    ans1: '',
    ans2: '',
    ans3: '',
    ans4: '',
    ans5: '',
    correct_ans: '',
    gr_ref: '',
  });

  const formatRuleNumber = (rule) => {
    if (!rule) return '';
    // API returns 'number' field (formatted) or raw gr_num/gr_sub
    if (rule.number) {
      return rule.number;
    }
    const num = rule.gr_num ?? 0;
    const sub = rule.gr_sub ?? 0;
    if (sub === 0 || sub === '0') {
      return `${num}`;
    }
    return `${num}.${sub}`;
  };

  const formatRuleName = useCallback((rule) => {
    if (!rule) return '';
    const ruleNum = formatRuleNumber(rule);
    // API returns 'description' field or raw gr_desc
    const desc = rule.description || rule.gr_desc || rule.title || '';
    return `${ruleNum} - ${desc}`;
  }, []);

  const applySearchFilter = useCallback((questionsList, term) => {
    const lowerTerm = term.toLowerCase().trim();
    const filtered = questionsList.filter((q) => {
      const questionText = (q.question || '').toLowerCase();
      const ans1 = (q.ans1 || '').toLowerCase();
      const ans2 = (q.ans2 || '').toLowerCase();
      const ans3 = (q.ans3 || '').toLowerCase();
      const ans4 = (q.ans4 || '').toLowerCase();
      const ans5 = (q.ans5 || '').toLowerCase();
      const correctAns = (q.correct_ans || '').toLowerCase();
      const questionId = String(q.question_id || '');
      
      // Find rule reference name if exists
      const ruleRef = rules.find(r => (r.id || r.gr_id) === q.gr_ref);
      const ruleName = ruleRef ? formatRuleName(ruleRef).toLowerCase() : '';
      
      return (
        questionText.includes(lowerTerm) ||
        ans1.includes(lowerTerm) ||
        ans2.includes(lowerTerm) ||
        ans3.includes(lowerTerm) ||
        ans4.includes(lowerTerm) ||
        ans5.includes(lowerTerm) ||
        correctAns.includes(lowerTerm) ||
        questionId.includes(lowerTerm) ||
        ruleName.includes(lowerTerm)
      );
    });
    setFilteredQuestions(filtered);
  }, [rules, formatRuleName]);

  const loadQuestions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await quizQuestionsApi.getAll();
      if (response.data.success) {
        const loadedQuestions = response.data.data || [];
        setQuestions(loadedQuestions);
        // Don't apply search filter here - let the searchTerm effect handle it
        setFilteredQuestions(loadedQuestions);
      } else {
        setError(response.data.message || 'Failed to load questions');
      }
    } catch (err) {
      console.error('Error loading questions:', err);
      setError(err.response?.data?.message || 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies - only load once

  const loadRules = useCallback(async () => {
    try {
      const response = await rulesApi.getAll(1, 1000); // Get all rules for dropdown
      if (response.data.success) {
        // Handle paginated response - data might be in response.data.data array
        const rulesData = Array.isArray(response.data.data) 
          ? response.data.data 
          : [];
        setRules(rulesData);
      }
    } catch (err) {
      console.error('Error loading rules:', err);
      console.error('Rules API response:', err.response?.data);
    }
  }, []);

  useEffect(() => {
    if (!authLoading) {
      if (!user || user.profile !== 'admin') {
        navigate('/profile');
        return;
      }
      loadRules();
      loadQuestions();
    }
  }, [user, authLoading, navigate, loadRules, loadQuestions]);

  // Apply search filter when searchTerm or questions change
  useEffect(() => {
    if (questions.length > 0) {
      if (searchTerm && searchTerm.trim() !== '') {
        applySearchFilter(questions, searchTerm);
      } else {
        setFilteredQuestions(questions);
      }
    }
  }, [searchTerm, questions, applySearchFilter]);

  const handleCreate = () => {
    setEditingQuestion(null);
    setFormData({
      question: '',
      ans1: '',
      ans2: '',
      ans3: '',
      ans4: '',
      ans5: '',
      correct_ans: '',
      gr_ref: '',
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setFormData({
      question: question.question || '',
      ans1: question.ans1 || '',
      ans2: question.ans2 || '',
      ans3: question.ans3 || '',
      ans4: question.ans4 || '',
      ans5: question.ans5 || '',
      correct_ans: question.correct_ans || '',
      // Convert gr_ref to string to match select value
      gr_ref: question.gr_ref ? String(question.gr_ref) : '',
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    if (term && term.trim() !== '') {
      applySearchFilter(questions, term);
    } else {
      setFilteredQuestions(questions);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setFilteredQuestions(questions);
  };

  const handleDeleteClick = (question) => {
    setQuestionToDelete(question);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!questionToDelete) return;

    try {
      setError('');
      await quizQuestionsApi.delete(questionToDelete.question_id);
      setSuccess('Question deleted successfully');
      setShowDeleteModal(false);
      setQuestionToDelete(null);
      setSearchTerm(''); // Clear search after delete
      loadQuestions();
    } catch (err) {
      console.error('Error deleting question:', err);
      setError(err.response?.data?.message || 'Failed to delete question');
      setShowDeleteModal(false);
    }
  };

  // Build the list of answer options that are actually provided (for TF/short sets)
  const getAvailableAnswerOptions = () => {
    const options = [];
    if (formData.ans1 && formData.ans1.trim()) options.push('A');
    if (formData.ans2 && formData.ans2.trim()) options.push('B');
    if (formData.ans3 && formData.ans3.trim()) options.push('C');
    if (formData.ans4 && formData.ans4.trim()) options.push('D');
    if (formData.ans5 && formData.ans5.trim()) options.push('E');
    return options;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.question || !formData.ans1 || !formData.ans2 || 
        !formData.correct_ans) {
      setError('Please fill in all required fields');
      return;
    }

    const availableOptions = getAvailableAnswerOptions();
    const correctUpper = formData.correct_ans.toUpperCase();

    if (!availableOptions.includes(correctUpper)) {
      setError('Correct answer must match one of the provided answers');
      return;
    }

    // Validate correct_ans is A, B, C, D, or E
    if (!['A', 'B', 'C', 'D', 'E'].includes(correctUpper)) {
      setError('Correct answer must be A, B, C, D, or E');
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      const submitData = {
        question: formData.question,
        ans1: formData.ans1,
        ans2: formData.ans2,
        ans3: formData.ans3,
        ans4: formData.ans4,
        correct_ans: correctUpper,
      };

      // Add optional fields
      if (formData.ans5) {
        submitData.ans5 = formData.ans5;
      }
      if (formData.gr_ref) {
        submitData.gr_ref = parseInt(formData.gr_ref);
      }

      if (editingQuestion) {
        await quizQuestionsApi.update(editingQuestion.question_id, submitData);
        setSuccess('Question updated successfully');
      } else {
        await quizQuestionsApi.create(submitData);
        setSuccess('Question created successfully');
      }

      setShowForm(false);
      setEditingQuestion(null);
      setSearchTerm(''); // Clear search after save
      loadQuestions();
    } catch (err) {
      console.error('Error saving question:', err);
      setError(err.response?.data?.message || 'Failed to save question');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingQuestion(null);
    setFormData({
      question: '',
      ans1: '',
      ans2: '',
      ans3: '',
      ans4: '',
      ans5: '',
      correct_ans: '',
      gr_ref: '',
    });
    setError('');
    setSuccess('');
  };

  if (authLoading || loading) {
    return (
      <>
        <Header />
        <div className="container">
          <Loading />
        </div>
        <Footer />
      </>
    );
  }

  const availableAnswerOptions = getAvailableAnswerOptions();

  return (
    <>
      <Header />
      <div className="container">
        <div className="quiz-questions-admin">
          <div className="admin-header">
            <h1>Quiz Questions Admin</h1>
            <Link to="/admin" className="btn btn-secondary back-btn">
              <i className="bi bi-arrow-left"></i> Back to GR Admin
            </Link>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <div className="admin-actions">
            <button onClick={handleCreate} className="btn btn-primary">
              <i className="bi bi-plus-circle"></i> Create New Question
            </button>
            <div className="admin-search">
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search questions by text, answers, or rule reference..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="admin-search-input"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="admin-search-clear"
                  title="Clear search"
                >
                  <i className="bi bi-x-circle"></i>
                </button>
              )}
            </div>
          </div>
          
          {searchTerm && (
            <div className="admin-search-results">
              Showing {filteredQuestions.length} of {questions.length} questions
            </div>
          )}

          {showForm && (
            <div className="admin-form">
              <h2>{editingQuestion ? 'Edit Question' : 'Create New Question'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="question">Question *</label>
                  <textarea
                    id="question"
                    name="question"
                    value={formData.question}
                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                    required
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="gr_ref">Rule Reference (Optional)</label>
                  <select
                    id="gr_ref"
                    name="gr_ref"
                    value={formData.gr_ref}
                    onChange={(e) => setFormData({ ...formData, gr_ref: e.target.value })}
                  >
                    <option value="">-- Select a Rule --</option>
                    {rules.map((rule) => {
                      // Use id (from RuleResource) or gr_id (raw) for the value
                      const ruleId = rule.id || rule.gr_id;
                      return (
                        <option key={ruleId} value={ruleId}>
                          {formatRuleName(rule)}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="ans1">Answer A *</label>
                    <input
                      type="text"
                      id="ans1"
                      name="ans1"
                      value={formData.ans1}
                      onChange={(e) => setFormData({ ...formData, ans1: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="ans2">Answer B *</label>
                    <input
                      type="text"
                      id="ans2"
                      name="ans2"
                      value={formData.ans2}
                      onChange={(e) => setFormData({ ...formData, ans2: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="ans3">Answer C (Optional)</label>
                    <input
                      type="text"
                      id="ans3"
                      name="ans3"
                      value={formData.ans3}
                      onChange={(e) => setFormData({ ...formData, ans3: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="ans4">Answer D (Optional)</label>
                    <input
                      type="text"
                      id="ans4"
                      name="ans4"
                      value={formData.ans4}
                      onChange={(e) => setFormData({ ...formData, ans4: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="ans5">Answer E (Optional)</label>
                  <input
                    type="text"
                    id="ans5"
                    name="ans5"
                    value={formData.ans5}
                    onChange={(e) => setFormData({ ...formData, ans5: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="correct_ans">Correct Answer *</label>
                  <select
                    id="correct_ans"
                    name="correct_ans"
                    value={formData.correct_ans}
                    onChange={(e) => setFormData({ ...formData, correct_ans: e.target.value })}
                    required
                  >
                    <option value="">-- Select --</option>
                    {availableAnswerOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    {editingQuestion ? 'Update Question' : 'Create Question'}
                  </button>
                  <button type="button" onClick={handleCancel} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Question</th>
                  <th>Rule Ref</th>
                  <th>Correct</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(searchTerm ? filteredQuestions : questions).length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center">
                      {searchTerm ? `No questions found matching "${searchTerm}"` : 'No questions found'}
                    </td>
                  </tr>
                ) : (
                  (searchTerm ? filteredQuestions : questions).map((question) => {
                    // Find rule by id (from RuleResource) or gr_id (raw)
                    const ruleRef = rules.find(r => (r.id || r.gr_id) === question.gr_ref);
                    return (
                      <tr key={question.question_id}>
                        <td>{question.question_id}</td>
                        <td className="question-text">{question.question}</td>
                        <td>
                          {ruleRef ? formatRuleName(ruleRef) : question.gr_ref || '-'}
                        </td>
                        <td>{question.correct_ans}</td>
                        <td className="actions-cell">
                          <button
                            onClick={() => handleEdit(question)}
                            className="btn btn-sm btn-secondary"
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            onClick={() => handleDeleteClick(question)}
                            className="btn btn-sm btn-danger"
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Footer />

      <ConfirmModal
        show={showDeleteModal}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteModal(false);
          setQuestionToDelete(null);
        }}
        title="Delete Question"
        message={questionToDelete ? `Are you sure you want to delete this question?` : 'Are you sure you want to delete this question?'}
      />
    </>
  );
}

export default QuizQuestions;
